import { useEffect, useRef, useState } from "react";

// Shared Pharos Vault hero: looping brand video, centred at 16:9 with a
// blurred poster filling the sides on wide screens. Muted autoplay (required
// by browsers); tap the speaker to hear the audio. Respects reduced motion.
const BASE = process.env.PUBLIC_URL || "";
const SRC_LG = `${BASE}/media/pharos-hero.mp4`;
const SRC_SM = `${BASE}/media/pharos-hero-sm.mp4`;
const SRC_WEBM = `${BASE}/media/pharos-hero.webm`;
const POSTER = `${BASE}/media/pharos-hero-poster.jpg`;

export default function VaultHero() {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [reduced, setReduced] = useState(false);
  const [small, setSmall] = useState(false);

  useEffect(() => {
    try {
      setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
      setSmall(window.innerWidth < 700);
    } catch (e) { /* ignore */ }
  }, []);

  // React does not reliably set the muted attribute, which some browsers
  // require before they allow autoplay — set it directly and start playback.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    setMuted(true);
    if (!reduced) v.play().catch(() => {});
  }, [small, reduced]);

  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <div style={{
      position: "relative", width: "100%", overflow: "hidden",
      height: "min(56.25vw, 440px)", background: "#050505",
      borderBottom: "2px solid #22c55e",
    }}>
      {/* Blurred fill for the sides on wide screens */}
      <div aria-hidden="true" style={{
        position: "absolute", inset: "-40px",
        backgroundImage: `url(${POSTER})`, backgroundSize: "cover", backgroundPosition: "center",
        filter: "blur(28px) brightness(0.35) saturate(1.3)",
      }} />
      {/* Centred 16:9 video */}
      <div style={{
        position: "relative", height: "100%", aspectRatio: "16 / 9", maxWidth: "100%",
        margin: "0 auto", boxShadow: "0 0 60px rgba(34,197,94,0.25)",
      }}>
        <video
          key={small ? "sm" : "lg"}
          ref={ref}
          poster={POSTER}
          autoPlay={!reduced}
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="Pharos Vault — from peptides to unique ingredients, we got your back"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        >
          <source src={small ? SRC_SM : SRC_LG} type="video/mp4" />
          <source src={SRC_WEBM} type="video/webm" />
        </video>
        {/* Edge fades so the video blends into the fill */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "linear-gradient(90deg, rgba(5,5,5,0.9) 0%, transparent 6%, transparent 94%, rgba(5,5,5,0.9) 100%)",
        }} />
        <button onClick={toggleSound} aria-label={muted ? "Turn sound on" : "Turn sound off"} style={{
          position: "absolute", right: 10, bottom: 10, width: 34, height: 34, borderRadius: "50%",
          border: "1px solid rgba(34,197,94,0.6)", background: "rgba(0,0,0,0.55)", color: "#fff",
          cursor: "pointer", fontSize: 15, lineHeight: "32px", padding: 0,
        }}>{muted ? "🔇" : "🔊"}</button>
      </div>
    </div>
  );
}
